import { useEffect, useMemo } from "react";
import { useShallow } from "zustand/react/shallow";

import { Input, Select } from "antd";
import { EVA_RESEARCH_NAME } from "constants/app.constants";
import { RADIUS_DIMENSION_MIN } from "constants/visualization.constants";
import styled from "styled-components/macro";

import { useDebounce, useLengthConversion } from "hooks";

import useFilteredGeoCalculationsJobs from "components/org-settings/hooks/useFilteredGeoCalculationsJobs";
import { useGetGeoCalculationsJobs } from "components/org-settings/hooks/useGetGeoCalculationsJobs";
import usePlayRadiusDefaults from "components/org-settings/hooks/usePlayRadiusDefaults";
import { GeoCalculationsJob } from "components/org-settings/models/GeoCalculations";
import { RADIUS_SECTIONS } from "components/org-settings/models/GeoModelDefaults";
import { StickCalculationsJob } from "components/org-settings/models/StickCalculations";

import { useXdaSettingsStore } from "../../context";
import { XDA_RADIUS_TYPES } from "../../context/types";
import {
  selectApplyRadiusPreset,
  selectXdaSettings
} from "../../context/xdaSettingsSelectors";
import { useVisDimensionsStore } from "../../visDimensionsStore";
import { XdaOptionItem, XdaOptionLabel, XdaOptionsWrapper } from "./XdaOptionsLayout";

// Helper function to extract dimensions from either job type
const getDimensionsFromJob = (job: GeoCalculationsJob | StickCalculationsJob) => {
  // Type guard to check if it's a GeoCalculationsJob
  if (
    "upHeight" in job &&
    "downHeight" in job &&
    "leftWidth" in job &&
    "rightWidth" in job
  ) {
    return {
      up: job.upHeight || 0,
      down: job.downHeight || 0,
      left: job.leftWidth || 0,
      right: job.rightWidth || 0
    };
  }

  // For StickCalculationsJob, try xdaDimensions first, then threeDvDimensions
  const dimensions =
    (job as StickCalculationsJob).xdaDimensions ||
    (job as StickCalculationsJob).threeDvDimensions;
  if (dimensions) {
    return {
      up: dimensions.upHeight || 0,
      down: dimensions.downHeight || 0,
      left: dimensions.leftWidth || 0,
      right: dimensions.rightWidth || 0
    };
  }

  // Default fallback
  return { up: 0, down: 0, left: 0, right: 0 };
};

const getDimValue = (
  dim: { [key: string]: number | string },
  camelKey: string,
  snakeKey: string
): number => (dim[camelKey] as number) ?? (dim[snakeKey] as number) ?? 0;

const XdaRadiusOptions = () => {
  const setDimensions = useVisDimensionsStore((s) => s.setDimensions);
  const settings = useXdaSettingsStore(useShallow(selectXdaSettings));
  const setXdaSettings = useXdaSettingsStore((s) => s.setXdaSettings);
  const applyRadiusPreset = useXdaSettingsStore(selectApplyRadiusPreset);
  const { downHeight, leftWidth, rightWidth, upHeight } = settings;
  const { data: jobs } = useGetGeoCalculationsJobs();
  const filteredJobs = useFilteredGeoCalculationsJobs(jobs);
  const { defaults: playDefaults, isLoading: playDefaultsLoading } =
    usePlayRadiusDefaults();

  const { convert, convertBack, getDisplayUnit } = useLengthConversion();
  const targetUnit = getDisplayUnit();

  // Build org defined radius options from play defaults, matched to active play
  const orgRadiusOptions = useMemo(() => {
    if (!playDefaults || playDefaults.length === 0) return {};
    // Only surface org-defined options for the play under the XDA line. If the
    // active play isn't known yet, or has no configured defaults, show nothing —
    // never fall back to an arbitrary play's radii.
    if (!settings.activeXdaPlay) return {};
    const relevantPlay = playDefaults.find((p) => p.play === settings.activeXdaPlay);
    if (!relevantPlay) return {};
    return RADIUS_SECTIONS.reduce((acc, section) => {
      const dim = relevantPlay.dimensions.find((d) => d.name === section);
      if (dim) {
        acc[`org_${section}`] = {
          up: getDimValue(dim, "upHeight", "up_height"),
          down: getDimValue(dim, "downHeight", "down_height"),
          left: getDimValue(dim, "leftWidth", "left_width"),
          right: getDimValue(dim, "rightWidth", "right_width")
        };
      }
      return acc;
    }, {} as Record<string, { up: number; down: number; left: number; right: number }>);
  }, [playDefaults, settings.activeXdaPlay]);

  // Build job radius options list
  const jobRadiusOptions = useMemo(() => {
    if (!filteredJobs || filteredJobs.length === 0) return {};

    return filteredJobs.reduce((acc, job) => {
      acc[job.name] = getDimensionsFromJob(job);
      return acc;
    }, {} as Record<string, { up: number; down: number; left: number; right: number }>);
  }, [filteredJobs]);

  const debouncedUpHeight = useDebounce(upHeight, 1200);
  const debouncedDownHeight = useDebounce(downHeight, 1200);
  const debouncedLeftWidth = useDebounce(leftWidth, 1200);
  const debouncedRightWidth = useDebounce(rightWidth, 1200);

  const DIMENSION_KEYS = new Set(["upHeight", "downHeight", "leftWidth", "rightWidth"]);

  // sync option changes with context
  const handleSettingChange = (key) => (value) => {
    let updatedValue = value;
    if (DIMENSION_KEYS.has(key)) {
      updatedValue = Math.max(RADIUS_DIMENSION_MIN, convertBack(value * 1, 6));
    }

    const nextSettings = { ...settings, [key]: updatedValue };
    setXdaSettings(nextSettings);
  };

  // Handle radius type selection
  const handleRadiusChange = (value) => {
    const preset = orgRadiusOptions[value] ?? jobRadiusOptions[value];
    if (preset) {
      // Org-defined or job-based radius — apply the resolved dimensions in
      // one named domain event (org takes precedence, matching the previous
      // if/else-if branch order).
      applyRadiusPreset({ selectedRadius: value, dims: preset });
    } else {
      // EVA Research types — update selectedRadius only
      handleSettingChange("selectedRadius")(value);
    }
  };

  // If an org-defined radius is selected but is no longer available for the
  // active play (e.g. the line moved to a play without org defaults), fall back
  // to the matching EVA Research radius so the selection stays valid. Wait until
  // the active play and defaults have resolved before deciding, so we don't
  // reset during loading.
  useEffect(() => {
    const { selectedRadius, activeXdaPlay } = settings;
    if (!selectedRadius.startsWith("org_")) return;
    if (playDefaultsLoading || !activeXdaPlay) return;
    if (orgRadiusOptions[selectedRadius]) return;

    const section = selectedRadius.slice("org_".length);
    const fallback = Object.values(XDA_RADIUS_TYPES).includes(section as XDA_RADIUS_TYPES)
      ? (section as XDA_RADIUS_TYPES)
      : XDA_RADIUS_TYPES.Gross;
    setXdaSettings({ ...settings, selectedRadius: fallback });
  }, [orgRadiusOptions, settings, playDefaultsLoading, setXdaSettings]);

  // sync width/height updates with the global VisDimensionsStore, shared with
  // every independently-mounted 3D-viewer widget on the dashboard
  useEffect(() => {
    setDimensions({
      upHeight: debouncedUpHeight,
      downHeight: debouncedDownHeight,
      leftWidth: debouncedLeftWidth,
      rightWidth: debouncedRightWidth
    });
  }, [
    debouncedLeftWidth,
    debouncedRightWidth,
    debouncedDownHeight,
    debouncedUpHeight,
    setDimensions
  ]);

  const handleTopChange = (value) => {
    const meterValue = Math.max(RADIUS_DIMENSION_MIN, convertBack(value * 1, 6));
    setXdaSettings({
      ...settings,
      upHeight: meterValue,
      downHeight: meterValue
    });
  };

  const handleLeftChange = (value) => {
    const meterValue = Math.max(RADIUS_DIMENSION_MIN, convertBack(value * 1, 6));
    setXdaSettings({
      ...settings,
      leftWidth: meterValue,
      rightWidth: meterValue
    });
  };

  const getDisplayValue = (value: number) => convert(value, 2);

  return (
    <XdaOptionsWrapper
      width={"300px"}
      paddingRight={"12px"}
      cardBodyPadding={"5px 5px 5px 5px"}
      data-testid="xda-radius-options">
      <XdaOptionItem>
        <XdaOptionLabel>Select Radius</XdaOptionLabel>
        <Select
          size="small"
          value={settings.selectedRadius}
          onChange={handleRadiusChange}>
          <Select.OptGroup label={EVA_RESEARCH_NAME}>
            <Select.Option value={XDA_RADIUS_TYPES.Gross}>Gross</Select.Option>
            <Select.Option value={XDA_RADIUS_TYPES.SweetSpot}>Sweet Spot</Select.Option>
            <Select.Option value={XDA_RADIUS_TYPES.LandingZone}>
              Landing Zone
            </Select.Option>
            <Select.Option value={XDA_RADIUS_TYPES.Custom}>Custom</Select.Option>
          </Select.OptGroup>
          {Object.keys(orgRadiusOptions).length > 0 && (
            <Select.OptGroup label="Org Defined Radius">
              {RADIUS_SECTIONS.filter(
                (section) => orgRadiusOptions[`org_${section}`]
              ).map((section) => (
                <Select.Option key={`org_${section}`} value={`org_${section}`}>
                  {section}
                </Select.Option>
              ))}
            </Select.OptGroup>
          )}
          {/* Job-based radius options with visual separation */}
          {Object.keys(jobRadiusOptions).length > 0 && (
            <Select.OptGroup label="Custom Calculation Radius">
              {Object.keys(jobRadiusOptions).map((jobName) => (
                <Select.Option key={jobName} value={jobName}>
                  {jobName}
                </Select.Option>
              ))}
            </Select.OptGroup>
          )}
        </Select>
      </XdaOptionItem>

      <XdaOptionItem>
        <XdaOptionLabel>Up ({targetUnit})</XdaOptionLabel>
        <OptionItemRightGroup>
          <InputWrapper
            disabled={settings.selectedRadius != XDA_RADIUS_TYPES.Custom}
            min={RADIUS_DIMENSION_MIN}
            onChange={(e) => handleTopChange(e.target.value)}
            size="small"
            step={1}
            type="number"
            value={getDisplayValue(upHeight)}
          />
        </OptionItemRightGroup>
      </XdaOptionItem>
      <XdaOptionItem>
        <XdaOptionLabel>Down ({targetUnit})</XdaOptionLabel>
        <InputWrapper
          disabled={settings.selectedRadius != XDA_RADIUS_TYPES.Custom}
          min={RADIUS_DIMENSION_MIN}
          onChange={(e) => handleSettingChange("downHeight")(e.target.value)}
          size="small"
          step={1}
          type={"number"}
          value={getDisplayValue(downHeight)}
        />
      </XdaOptionItem>
      <SmallPadding />
      <XdaOptionItem>
        <XdaOptionLabel>Left ({targetUnit})</XdaOptionLabel>
        <OptionItemRightGroup>
          <InputWrapper
            disabled={settings.selectedRadius != XDA_RADIUS_TYPES.Custom}
            min={RADIUS_DIMENSION_MIN}
            onChange={(e) => handleLeftChange(e.target.value)}
            size="small"
            step={10}
            type={"number"}
            value={getDisplayValue(leftWidth)}
          />
        </OptionItemRightGroup>
      </XdaOptionItem>
      <XdaOptionItem>
        <XdaOptionLabel>Right ({targetUnit})</XdaOptionLabel>
        <InputWrapper
          disabled={settings.selectedRadius != XDA_RADIUS_TYPES.Custom}
          min={RADIUS_DIMENSION_MIN}
          onChange={(e) => handleSettingChange("rightWidth")(e.target.value)}
          size="small"
          step={10}
          type={"number"}
          value={getDisplayValue(rightWidth)}
        />
      </XdaOptionItem>
    </XdaOptionsWrapper>
  );
};

export default XdaRadiusOptions;

const InputWrapper = styled(Input)`
  max-width: 100px;
  border-radius: var(--border-radius);
`;

const OptionItemRightGroup = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  gap: 5px;

  .ant-slider {
    width: 80px;
  }

  .ant-input-number {
    max-width: 130px;
    width: 130px;
  }

  .ant-select {
    width: 130px;
  }

  .ant-btn {
    border-radius: 5px;
  }

  .ant-btn-circle {
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .ant-btn:hover {
    background-color: var(--color-primary);
    border-color: var(--color-primary);
  }

  padding: 2px 0;
`;

const SmallPadding = styled.div`
  padding-top: 8px;
  padding-bottom: 8px;
`;
